import React, { useEffect, useMemo, useRef, useState } from "react"
import { X, Plus, Edit, Trash2, Info } from "lucide-react"
import { useParams } from "react-router"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@f2e/ui/components/base"
import {
  useCreateEvaluatorApi,
  useUpdateEvaluatorApi,
  useDeleteEvaluatorApi,
  useSuggestEvaluatorSystemPromptApi,
  makeCreateEvaluatorRequest,
  makeUpdateEvaluatorRequest,
  createEvaluatorResponseToEvaluator,
  updateEvaluatorResponseToEvaluator,
} from "@/hooks/api/evaluators"
import AITextarea from "@/components/Conversation/AITextarea"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { safe } from "@/components/Copilot/summarizers"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { useStore } from "@/store"
import {
  LLMModelSelect,
  SYSTEM_DEFAULT_LLM_MODEL_VALUE,
} from "@/components/Common/LLMModelSelect"

import { Evaluator } from "./types"
import { getEvaluatorModelOptions } from "./evaluatorModelOptions"

interface EvaluatorModalProps {
  evaluators: Evaluator[]
  open: boolean
  refreshEvaluators: () => void
  onEvaluatorCreated?: (evaluator: Evaluator) => void
  onEvaluatorUpdated?: (evaluator: Evaluator) => void
  onClose: () => void
}

const EvaluatorModal = ({
  evaluators,
  open,
  refreshEvaluators,
  onEvaluatorCreated,
  onEvaluatorUpdated,
  onClose,
}: EvaluatorModalProps) => {
  const { workspaceId } = useParams()
  const [view, setView] = useState<"list" | "form">("list")
  const [editingEvaluator, setEditingEvaluator] = useState<Evaluator | null>(
    null
  )
  const [form, setForm] = useState<{
    name: string
    description: string
    systemPrompt: string
    judgeLlmModelId: string
  }>({
    name: "",
    description: "",
    systemPrompt: "",
    judgeLlmModelId: SYSTEM_DEFAULT_LLM_MODEL_VALUE,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingEvaluatorId, setDeletingEvaluatorId] = useState<string | null>(
    null
  )
  const [evaluatorPendingDelete, setEvaluatorPendingDelete] =
    useState<Evaluator | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fetchFn: createEvaluator, data: createResponse } =
    useCreateEvaluatorApi()
  const { fetchFn: updateEvaluator, data: updateResponse } =
    useUpdateEvaluatorApi()
  const { fetchFn: deleteEvaluator } = useDeleteEvaluatorApi()
  const { t } = useTranslation()
  const availableModels = useStore((state) => state.availableModels)
  const { models: judgeModelOptions, hasUnavailableSelection } = useMemo(
    () =>
      getEvaluatorModelOptions(
        availableModels,
        editingEvaluator?.judgeLlmModelId,
        form.judgeLlmModelId,
        t(
          "evaluation.evaluationTable.evaluatorModal.form.unavailableModelLabel"
        )
      ),
    [
      availableModels,
      editingEvaluator?.judgeLlmModelId,
      form.judgeLlmModelId,
      t,
    ]
  )
  const [isGeneratingSystemPrompt, setIsGeneratingSystemPrompt] =
    useState(false)
  const suggestionAbortControllerRef = useRef<AbortController | null>(null)
  const { fetchFn: suggestEvaluatorSystemPrompt } =
    useSuggestEvaluatorSystemPromptApi()

  const handleGenerateSystemPrompt = async (instruction?: string) => {
    if (!workspaceId || !instruction?.trim()) {
      console.warn("Instruction is required to generate system prompt.")
      return
    }

    suggestionAbortControllerRef.current?.abort()
    const controller = new AbortController()
    suggestionAbortControllerRef.current = controller
    setFormError(null)
    setIsGeneratingSystemPrompt(true)

    try {
      const response = await suggestEvaluatorSystemPrompt({
        urlBuilderParams: { workspace_id: workspaceId },
        jsonBody: {
          name: form.name,
          instruction: instruction.trim(),
          primary_contents: form.description,
          system_prompt: form.systemPrompt || undefined,
        },
        retry: 0,
        timeout: false,
        signal: controller.signal,
      })
      const suggestedSystemPrompt = response?.data?.system_prompt
      if (suggestedSystemPrompt) {
        setForm((current) => ({
          ...current,
          systemPrompt: suggestedSystemPrompt,
        }))
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setFormError(
          getUserFacingApiErrorMessage(
            error,
            t,
            t(
              "evaluation.evaluationTable.evaluatorModal.error.generateSystemPromptFailed"
            )
          )
        )
      }
    } finally {
      if (suggestionAbortControllerRef.current === controller) {
        suggestionAbortControllerRef.current = null
        setIsGeneratingSystemPrompt(false)
      }
    }
  }

  const handleCancelGenerateSystemPrompt = () => {
    suggestionAbortControllerRef.current?.abort()
    suggestionAbortControllerRef.current = null
    setIsGeneratingSystemPrompt(false)
  }

  useEffect(
    () => () => {
      suggestionAbortControllerRef.current?.abort()
    },
    []
  )

  // Reset view and editingEvaluator every time modal is opened
  useEffect(() => {
    if (open) {
      setView("list")
      setEditingEvaluator(null)
      setEvaluatorPendingDelete(null)
      setDeleteError(null)
    }
  }, [open])

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
    return () => {
      document.body.classList.remove("overflow-hidden")
    }
  }, [open])

  if (!open) return null

  const handleEdit = (evaluator: Evaluator) => {
    setEditingEvaluator(evaluator)
    setForm({
      name: evaluator.name,
      description: evaluator.description || "",
      systemPrompt: evaluator.systemPromptTemplate || "",
      judgeLlmModelId:
        evaluator.judgeLlmModelId || SYSTEM_DEFAULT_LLM_MODEL_VALUE,
    })
    setView("form")
  }

  const handleAdd = () => {
    setEditingEvaluator(null)
    setForm({
      name: "",
      description: "",
      systemPrompt: "",
      judgeLlmModelId: SYSTEM_DEFAULT_LLM_MODEL_VALUE,
    })
    setView("form")
  }

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    setIsSubmitting(true)

    if (!form.name.trim()) {
      setFormError(
        t("evaluation.evaluationTable.evaluatorModal.error.nameRequired")
      )
      setIsSubmitting(false)
      return
    }
    if (!form.systemPrompt.trim()) {
      setFormError(
        t(
          "evaluation.evaluationTable.evaluatorModal.error.systemPromptRequired"
        )
      )
      setIsSubmitting(false)
      return
    }
    if (
      !form.systemPrompt.includes("{output}") &&
      !form.systemPrompt.includes("{tool_steps}")
    ) {
      setFormError(
        t("evaluation.evaluationTable.evaluatorModal.error.systemPromptFormat")
      )
      setIsSubmitting(false)
      return
    }

    try {
      const isEditing = editingEvaluator !== null

      if (isEditing) {
        const requestData = makeUpdateEvaluatorRequest(
          {
            name: form.name,
            description: form.description,
            systemPromptTemplate: form.systemPrompt,
            judgeLlmModelId:
              form.judgeLlmModelId === SYSTEM_DEFAULT_LLM_MODEL_VALUE
                ? null
                : form.judgeLlmModelId,
          },
          editingEvaluator.judgeLlmModelId ?? null
        )
        await updateEvaluator({
          urlBuilderParams: { evaluator_id: editingEvaluator.id },
          jsonBody: requestData,
        })

        // Use the response data to update the local state
        if (updateResponse?.data) {
          const updatedEvaluator = updateEvaluatorResponseToEvaluator(
            updateResponse.data
          )
          onEvaluatorUpdated?.(updatedEvaluator)
        }
      } else {
        const requestData = makeCreateEvaluatorRequest(
          form.name,
          form.systemPrompt,
          workspaceId!,
          form.description,
          form.judgeLlmModelId === SYSTEM_DEFAULT_LLM_MODEL_VALUE
            ? null
            : form.judgeLlmModelId
        )
        await createEvaluator({
          jsonBody: requestData,
        })

        // Use the response data to update the local state
        if (createResponse?.data) {
          const newEvaluator = createEvaluatorResponseToEvaluator(
            createResponse.data
          )
          onEvaluatorCreated?.(newEvaluator)
        }
      }

      // Reset form
      setForm({
        name: "",
        description: "",
        systemPrompt: "",
        judgeLlmModelId: SYSTEM_DEFAULT_LLM_MODEL_VALUE,
      })
      setEditingEvaluator(null)
      setView("list")

      // Also refresh evaluators as a fallback
      refreshEvaluators()
    } catch (err) {
      const operation = editingEvaluator
        ? t("evaluation.evaluationTable.evaluatorModal.error.updateFailed")
        : t("evaluation.evaluationTable.evaluatorModal.error.createFailed")
      setFormError(getUserFacingApiErrorMessage(err, t, operation))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (evaluatorId: string) => {
    if (!workspaceId) return
    setDeletingEvaluatorId(evaluatorId)
    setDeleteError(null)
    try {
      await deleteEvaluator({
        urlBuilderParams: { evaluator_id: evaluatorId },
      })
      setEvaluatorPendingDelete(null)
      refreshEvaluators()
    } catch (err) {
      setDeleteError(
        getUserFacingApiErrorMessage(
          err,
          t,
          t("evaluation.evaluationTable.evaluatorModal.error.deleteFailed")
        )
      )
    } finally {
      setDeletingEvaluatorId(null)
    }
  }

  const isEditing = editingEvaluator !== null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-all">
      <div className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-fadeIn">
        <CopilotHiddenField
          fieldKey="evaluatorModal.summary"
          value={`view=${view}; editing=${isEditing ? "true" : "false"}; editingId=${editingEvaluator?.id || ""}; editingName=${safe(editingEvaluator?.name || "")}; list=${evaluators
            .map(
              (ev) =>
                `name=${safe(ev.name)}; model=${safe(ev.judgeLlmModelId || "system-default")}; desc=${safe(ev.description || "")}; systemPrompt=${safe(ev.systemPromptTemplate || "")}`
            )
            .join(" | ")}`}
        />
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-950 sticky top-0 z-10">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {view === "form"
              ? isEditing
                ? t("evaluation.evaluationTable.evaluatorModal.title.edit")
                : t("evaluation.evaluationTable.evaluatorModal.title.add")
              : t("evaluation.evaluationTable.evaluatorModal.title.manage")}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full p-2 focus:outline-hidden focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-600"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50 dark:bg-slate-900">
          {view === "list" && (
            <>
              <div className="flex flex-col gap-6">
                {/* Add button */}
                <div className="flex justify-end">
                  <button
                    className="flex items-center gap-2 rounded-lg border border-blue-600 dark:border-blue-400 bg-blue-600 dark:bg-blue-400 px-5 py-2 text-sm font-medium text-white dark:text-gray-900 shadow-xs hover:border-blue-700 dark:hover:border-blue-500 hover:bg-blue-700 dark:hover:bg-blue-500 transition-all"
                    onClick={handleAdd}
                  >
                    <Plus className="w-4 h-4" />{" "}
                    {t("evaluation.evaluationTable.evaluatorModal.button.add")}
                  </button>
                </div>
                {/* Error */}
                {deleteError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md text-red-700 dark:text-red-200 text-sm">
                    {deleteError}
                  </div>
                )}
                {/* Evaluator List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {evaluators.length === 0 ? (
                    <div className="col-span-2 text-center py-8 text-gray-400 dark:text-gray-500">
                      {t(
                        "evaluation.evaluationTable.evaluatorModal.list.empty"
                      )}
                    </div>
                  ) : (
                    evaluators.map((ev) => (
                      <div
                        key={ev.id}
                        className="flex flex-col justify-between bg-white dark:bg-slate-950 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-5 transition hover:shadow-lg group relative"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-lg text-gray-900 dark:text-gray-100 truncate max-w-48">
                            {ev.name}
                          </span>
                          {ev.description && (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    tabIndex={0}
                                    className="ml-1 cursor-pointer"
                                  >
                                    <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs whitespace-normal text-sm p-3 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-700"
                                >
                                  <span>{ev.description}</span>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-4 truncate whitespace-nowrap overflow-hidden max-w-full">
                          <div className="not-italic mb-1">
                            {t(
                              "evaluation.evaluationTable.evaluatorModal.list.modelPrefix"
                            )}
                            {ev.judgeLlmModelId
                              ? availableModels.find(
                                  (model) =>
                                    model.modelId === ev.judgeLlmModelId
                                )?.displayName || ev.judgeLlmModelId
                              : t(
                                  "evaluation.evaluationTable.evaluatorModal.form.systemDefaultModel"
                                )}
                          </div>
                          {ev.systemPromptTemplate && (
                            <span className="italic">
                              {t(
                                "evaluation.evaluationTable.evaluatorModal.list.promptPrefix"
                              )}
                              {ev.systemPromptTemplate.slice(0, 60)}
                              {ev.systemPromptTemplate.length > 60 ? "..." : ""}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 absolute top-4 right-4">
                          <button
                            className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400 transition-colors"
                            onClick={() => handleEdit(ev)}
                            title={t(
                              "evaluation.evaluationTable.evaluatorModal.button.edit"
                            )}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            className={`p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900 text-red-500 dark:text-red-400 transition-colors ${deletingEvaluatorId === ev.id ? "opacity-50 cursor-not-allowed" : ""}`}
                            onClick={() => {
                              setDeleteError(null)
                              setEvaluatorPendingDelete(ev)
                            }}
                            title={t(
                              "evaluation.evaluationTable.evaluatorModal.button.delete"
                            )}
                            disabled={deletingEvaluatorId === ev.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
          {view === "form" && (
            <form
              className="max-w-2xl mx-auto bg-white dark:bg-slate-950 rounded-xl shadow-sm p-8 flex flex-col gap-6 animate-fadeIn"
              onSubmit={handleFormSubmit}
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("evaluation.evaluationTable.evaluatorModal.form.name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      data-copilot-field="evaluatorModal.form.name"
                      data-copilot-value={form.name}
                      value={form.name}
                      onChange={handleFormChange}
                      className="block w-full rounded-md border-gray-300 dark:border-slate-700 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                      placeholder={t(
                        "evaluation.evaluationTable.evaluatorModal.form.namePlaceholder"
                      )}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t(
                        "evaluation.evaluationTable.evaluatorModal.form.description"
                      )}
                    </label>
                    <textarea
                      name="description"
                      rows={4}
                      data-copilot-field="evaluatorModal.form.description"
                      data-copilot-value={form.description}
                      value={form.description}
                      onChange={handleFormChange}
                      className="block w-full rounded-md border-gray-300 dark:border-slate-700 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-gray-100 min-h-[150px] max-h-[450px]"
                      placeholder={t(
                        "evaluation.evaluationTable.evaluatorModal.form.descriptionPlaceholder"
                      )}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                      {t(
                        "evaluation.evaluationTable.evaluatorModal.form.judgeModel"
                      )}
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="ml-1 cursor-pointer">
                              <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs whitespace-normal text-sm p-3 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-700"
                          >
                            {t(
                              "evaluation.evaluationTable.evaluatorModal.form.judgeModelHelp"
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <LLMModelSelect
                      models={judgeModelOptions}
                      value={form.judgeLlmModelId}
                      onValueChange={(judgeLlmModelId) =>
                        setForm((current) => ({
                          ...current,
                          judgeLlmModelId,
                        }))
                      }
                      placeholder={t(
                        "evaluation.evaluationTable.evaluatorModal.form.judgeModelPlaceholder"
                      )}
                      defaultOption={{
                        value: SYSTEM_DEFAULT_LLM_MODEL_VALUE,
                        label: t(
                          "evaluation.evaluationTable.evaluatorModal.form.systemDefaultModel"
                        ),
                        description: t(
                          "evaluation.evaluationTable.evaluatorModal.form.systemDefaultModelDescription"
                        ),
                      }}
                      testId="evaluator-judge-model-select"
                      copilotField="evaluatorModal.form.judgeLlmModelId"
                      pricingNote={t(
                        "workspace.agent.basicForm.llmModelPricingNote",
                        "Actual cost is based on token usage"
                      )}
                    />
                    {hasUnavailableSelection && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                        {t(
                          "evaluation.evaluationTable.evaluatorModal.form.unavailableModelDescription"
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                      {t(
                        "evaluation.evaluationTable.evaluatorModal.form.systemPrompt"
                      )}
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="ml-1 cursor-pointer">
                              <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs whitespace-normal text-sm p-3 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-700"
                          >
                            <div className="flex flex-col">
                              <span>
                                {t(
                                  "evaluation.evaluationTable.evaluatorModal.form.systemPromptTooltip1"
                                )}
                              </span>
                              <span>
                                {t(
                                  "evaluation.evaluationTable.evaluatorModal.form.systemPromptTooltip2"
                                )}
                              </span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <AITextarea
                      name="systemPrompt"
                      defaultValue={form.systemPrompt}
                      placeholder={t(
                        "evaluation.evaluationTable.evaluatorModal.form.systemPromptPlaceholder"
                      )}
                      aiTooltip={t(
                        "evaluation.evaluationTable.evaluatorModal.form.generateSystemPromptTooltip"
                      )}
                      aiLoadingTooltip={t(
                        "evaluation.evaluationTable.evaluatorModal.form.generateSystemPromptLoadingTooltip"
                      )}
                      withInstruction={true}
                      isLoading={isGeneratingSystemPrompt}
                      onGenerate={handleGenerateSystemPrompt}
                      onCancel={handleCancelGenerateSystemPrompt}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, systemPrompt: e.target.value }))
                      }
                      required
                    />
                    <CopilotHiddenField
                      fieldKey="evaluatorModal.form.systemPrompt"
                      value={form.systemPrompt}
                    />
                  </div>
                </div>
              </div>
              {formError && (
                <div className="text-red-500 dark:text-red-300 text-sm bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-3">
                  {formError}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-xs hover:bg-gray-50 dark:hover:bg-slate-800"
                  onClick={() => setView("list")}
                  disabled={isSubmitting}
                >
                  {t("evaluation.evaluationTable.evaluatorModal.button.cancel")}
                </button>
                <button
                  type="submit"
                  className={`rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white dark:text-gray-900 shadow-xs ${
                    isSubmitting
                      ? "bg-gray-400 dark:bg-gray-700 cursor-not-allowed"
                      : "bg-blue-600 dark:bg-blue-400 hover:bg-blue-700 dark:hover:bg-blue-500"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t(
                        "evaluation.evaluationTable.evaluatorModal.button.saving"
                      )
                    : isEditing
                      ? t(
                          "evaluation.evaluationTable.evaluatorModal.button.update"
                        )
                      : t(
                          "evaluation.evaluationTable.evaluatorModal.button.save"
                        )}
                </button>
              </div>
            </form>
          )}
        </div>
        <AlertDialog
          open={evaluatorPendingDelete !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen && !deletingEvaluatorId) {
              setEvaluatorPendingDelete(null)
              setDeleteError(null)
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("evaluation.evaluationTable.evaluatorModal.delete.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  "evaluation.evaluationTable.evaluatorModal.delete.description",
                  {
                    name: evaluatorPendingDelete?.name,
                    count: evaluatorPendingDelete?.evaluationResultCount ?? 0,
                  }
                )}
              </AlertDialogDescription>
              {deleteError && (
                <p
                  role="alert"
                  className="text-sm text-red-600 dark:text-red-300"
                >
                  {deleteError}
                </p>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingEvaluatorId !== null}>
                {t("evaluation.evaluationTable.evaluatorModal.delete.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={deletingEvaluatorId !== null}
                onClick={(event) => {
                  event.preventDefault()
                  if (evaluatorPendingDelete) {
                    void handleDelete(evaluatorPendingDelete.id)
                  }
                }}
              >
                {deletingEvaluatorId
                  ? t(
                      "evaluation.evaluationTable.evaluatorModal.delete.deleting"
                    )
                  : t(
                      "evaluation.evaluationTable.evaluatorModal.delete.confirm"
                    )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}

export default EvaluatorModal
